import * as React from "react";
import * as analytics from '../../../analytics';
import { connect } from "react-redux";
import { IP4WProject, actionCreators, IP4WConnectionState } from "../../../store/integration/P4WStore";
import { IImportProjectMap } from "../../../store/integration/common";
import { ApplicationState } from "../../../store/index";
import { useDataLoadingAfterConnectionValidationEffect, IImportEntity, IImportEntityMap, getValidationError } from "./common";
import { toDictionaryById } from "../../utils/common";
import { ProjectsImportProps } from "./ProjectsImportPanel";
import { UserState } from "../../../store/User";

type OwnProps = {
    connectionId: string;
    onRender: (props: ProjectsImportProps) => JSX.Element;
}

type StateProps = {
    user: UserState;
    projects: IP4WProject[];
    isLoading: boolean;
    error: string | null;
    isImporting: boolean;
    connections: IP4WConnectionState;
}

type Props = OwnProps & StateProps & typeof actionCreators;
const P4WProjectImport = (props: Props) => {
    const { connectionId } = props;
    const entities = React.useMemo(() => props.projects.map<IImportEntity>(_ => ({
        id: _.id,
        name: _.name,
        fullName: `${_.name} (${_.id})`,
        isAlreadyLinked: _.isAlreadyLinked
    })), [props.projects]);
    const map = React.useMemo(() => toDictionaryById(props.projects), [props.projects]);
    const _onImport = React.useCallback((connectionId: string, maps: IImportEntityMap[]) => {
        props.importProjects(connectionId, maps.map<IImportProjectMap<IP4WProject>>(_ => ({
            linkingData: map[_.entityId],
            projectId: _.projectId,
            projectName: _.projectName
        })))
        analytics.trackImport('Import Projects from P4W', props.user, { count: maps.length });
        }, [map]);

    const connectionValidation = useDataLoadingAfterConnectionValidationEffect(
        connectionId, 
        props.connections, 
        props.isImporting, 
        props.verifyConnection,
        props.loadProjects
    );

    return props.onRender({
        label: "Projects",
        connectionId: props.connectionId,
        isLoading: connectionValidation?.isValidating || props.isLoading,
        error: getValidationError(connectionValidation) ?? props.error,
        onImport: _onImport,

        oldImportGridProps: {
            entities: entities,
            getSourcesInfo: __ => __.sourceInfos.map(_ => ({
                sourceId: _.sourceData.projectId,
                connectionId: _.connectionId,
                data: _
            }))
        }
    });
}

function mapStateToProps(state: ApplicationState, ownProps?: OwnProps): StateProps {
    return {
        user: state.user,
        projects: state.p4w.projects.entities,
        isLoading: state.p4w.projects.isLoading,
        error: state.p4w.projects.error,
        isImporting: state.import.projects.isImporting,
        connections: state.p4w.connections
    }
}

export default connect(mapStateToProps, actionCreators)(P4WProjectImport);